/**
 * Server-computed queue metrics.
 *
 * Every number here comes from `queue_summary` — none of it may be derived from
 * the loaded page. The oldest pending alert in particular usually is NOT on the
 * current cursor page, so computing it from `items` would quietly report the
 * oldest alert the operator happens to be looking at.
 */
import { useTranslation } from "react-i18next";
import { Clock, Hourglass, Layers, ListChecks } from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { waitedFor } from "@/alerts/utils/format";
import { describeCarryover } from "@/alerts/utils/carryover";
import { alertTypeLabel } from "@/alerts/types/machine-alert";
import type { AlertQueueSummary } from "@/alerts/types/alerts-workspace";

const Tile = ({
  icon: Icon,
  label,
  children,
  hint,
}: {
  icon: typeof Clock;
  label: string;
  children: React.ReactNode;
  hint?: string;
}) => (
  <div className="flex min-w-0 flex-1 items-start gap-2.5 px-4 py-3">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="truncate text-sm font-medium">{children}</div>
      {hint && <p className="truncate text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  </div>
);

/** Approximate values get the same `~` + dotted treatment as the row ages. */
const Approx = ({ estimated, children }: { estimated: boolean; children: React.ReactNode }) => {
  const { t } = useTranslation("alerts");
  return (
    <span
      className={cn(
        estimated && "underline decoration-dotted underline-offset-2",
      )}
      title={estimated ? t("queue.summary.approxTitle") : undefined}
    >
      {estimated ? "~" : ""}
      {children}
    </span>
  );
};

const CarryoverValue = ({ summary }: { summary: AlertQueueSummary }) => {
  const { t } = useTranslation("alerts");
  // `describeCarryover` owns the three-state distinction (available / timezone
  // fallback / unavailable) and the rule that a null count is never a zero.
  const display = describeCarryover(summary.carryover);
  if (display.unavailable) {
    return (
      <span className="text-muted-foreground" title={display.reason ?? undefined}>
        {t("queue.summary.carryoverUnavailable")}
      </span>
    );
  }
  return (
    <span className="tabular-nums">
      <Approx estimated={summary.carryover.estimated_count > 0}>{display.value}</Approx>
    </span>
  );
};

export const AlertQueueSummaryBar = ({
  summary,
  count,
  asOf,
}: {
  summary: AlertQueueSummary;
  count: number;
  asOf: string;
}) => {
  const { t } = useTranslation("alerts");
  const topType = [...summary.by_alert_type].sort((a, b) => b.count - a.count)[0];
  const { carryover } = summary;

  return (
    <div className="grid grid-cols-2 divide-x divide-y divide-border border-b border-border sm:grid-cols-4 sm:divide-y-0">
      <Tile
        icon={ListChecks}
        label={t("queue.summary.pendingInScope")}
        hint={t("queue.summary.asOf", { ago: waitedFor(asOf) })}
      >
        <span className="tabular-nums">{count}</span>
        {summary.estimated_age_count > 0 && (
          <span className="ml-2 text-[11px] font-normal text-muted-foreground">
            {t("queue.summary.approxAges", { n: summary.estimated_age_count })}
          </span>
        )}
      </Tile>

      <Tile
        icon={Hourglass}
        label={t("queue.summary.oldestPending")}
        hint={
          summary.oldest_pending
            ? t("queue.summary.oldestAlertId", { id: summary.oldest_pending.alert_id })
            : undefined
        }
      >
        {summary.oldest_pending ? (
          <Approx estimated={summary.oldest_pending.estimated}>
            {waitedFor(summary.oldest_pending.waiting_since, asOf)}
          </Approx>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </Tile>

      <Tile
        icon={Layers}
        label={t("queue.summary.carryover")}
        hint={
          carryover.count == null
            ? undefined
            : carryover.timezone_fallback
              ? // A fallback is a SUCCESS — say which block it was measured
                // against rather than dressing it up as a failure.
                // `block` rather than `context`: i18next reserves `context` as
                // an option name, so a variable called that never interpolates.
                t("queue.summary.carryoverContext", {
                  block: describeCarryover(carryover).context ?? "",
                  ago: waitedFor(carryover.block_started_at, asOf),
                })
              : t("queue.summary.carryoverSince", {
                  ago: waitedFor(carryover.block_started_at, asOf),
                })
        }
      >
        <CarryoverValue summary={summary} />
      </Tile>

      <Tile
        icon={Clock}
        label={t("queue.summary.largestType")}
        hint={topType ? t("queue.summary.typePending", { n: topType.count }) : undefined}
      >
        {topType ? alertTypeLabel(topType.type) : <span className="text-muted-foreground">—</span>}
      </Tile>
    </div>
  );
};

